import { useMemo, useState, type ReactNode } from "react"
import ComposedChart, {
  type TGraphProps,
} from "~/ui-rtk/components/ui/charts/ComposedChart/ComposedChart"
import ChartResizeHandle from "~/ui-rtk/components/ui/charts/ChartResizeHandle/ChartResizeHandle"
import Chip from "~/ui-rtk/components/ui/common/Chip"
import type { AggregationType } from "~/ui-rtk/constants/charts"
import { getLightColor, stringToColor } from "~/ui-rtk/utils/chart-utils"
import type { MetricDefinition } from "~/shared/kpi"
import type { ColumnBucket, KpiRow } from "../utils/custom-metric-rows"
import { useChartHeight } from "../hooks/useChartHeight"
import {
  DEFAULT_CHART_HEIGHT,
  MAX_CHART_HEIGHT,
  MIN_CHART_HEIGHT,
} from "../constants/settings"

// Chart rendering for a custom-report group: the same rows the group's table
// shows, plotted per column bucket as lines or grouped bars. The chart
// REPLACES the table (v1 has no totals footer, growth rows, or inline
// editing); switching back to the table restores those.

export type ChartVisualization = "line" | "bar"

/**
 * Null-preserving numeric coercion for chart points. Cube delivers STRING
 * measures on the compareDateRange path, so values must be coerced — but
 * never with `|| 0`: an absent bucket must stay `null` so recharts renders a
 * gap (Line's connectNulls is false), not a fake zero.
 */
export function coerceChartValue(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null
  const num = Number(value)
  return Number.isNaN(num) ? null : num
}

/**
 * One chart row per column bucket, dated by the bucket's ISO start — NEVER
 * parsed back out of a formatted column header. Reads only the plain bucket
 * key per row (the reserved `_yoy_change` / `_warning` / `_warning_prev`
 * siblings are never touched); with YoY on, the `_yoy_previous` sibling is
 * additionally emitted under `compared.<key>`, which ComposedChart's
 * compareMode renders as a dashed same-color line / hatched bar.
 */
export function buildGroupChartData(
  rows: KpiRow[],
  columnBuckets: ColumnBucket[],
  showYoY: boolean,
): Record<string, string | number | null>[] {
  return columnBuckets.map(bucket => {
    const point: Record<string, string | number | null> = {
      date: bucket.start,
    }
    for (const row of rows) {
      const key = row.key
      if (typeof key !== "string" || key === "") continue
      point[key] = coerceChartValue(row[bucket.key])
      if (showYoY) {
        point[`compared.${key}`] = coerceChartValue(
          row[`${bucket.key}_yoy_previous`],
        )
      }
    }
    return point
  })
}

/**
 * Unit families sharing a y-axis. ComposedChart renders at most two visible
 * axes, so at most two families chart at once.
 */
type UnitFamily = "currency" | "count" | "ratio"

function familyForType(type: string | undefined): UnitFamily {
  if (type === "currency") return "currency"
  if (
    type === "percentage" ||
    type === "percent" ||
    type === "decimal" ||
    type === "coef"
  ) {
    return "ratio"
  }
  return "count"
}

export interface GroupChartSeries {
  graphs: TGraphProps[]
  /** Labels of metrics hidden by the two-axis unit-family limit. */
  excludedLabels: string[]
}

/**
 * One graph per row in the group's configured order (rows may include
 * auto-inserted typed custom-metric rows — those chart as ordinary series,
 * labeled from the row itself). The first two unit families encountered get
 * the two y-axes; metrics of any further family are EXCLUDED and surfaced in
 * a visible notice — never silently plotted against a hidden axis.
 */
export function buildGroupChartGraphs(
  rows: KpiRow[],
  metricDefs: MetricDefinition[],
  visualization: ChartVisualization,
): GroupChartSeries {
  const defByKey = new Map(metricDefs.map(def => [def.key, def]))
  const axisFamilies = new Set<UnitFamily>()
  const graphs: TGraphProps[] = []
  const excludedLabels: string[] = []

  for (const row of rows) {
    const key = row.key
    if (typeof key !== "string" || key === "") continue
    const def = defByKey.get(key)
    const label =
      def?.label ?? (typeof row.metric === "string" ? row.metric : key)
    const type = def?.type ?? (typeof row.type === "string" ? row.type : "")
    const suffix =
      def?.suffix ?? (typeof row.suffix === "string" ? row.suffix : undefined)
    const family = familyForType(type)
    if (!axisFamilies.has(family)) {
      if (axisFamilies.size >= 2) {
        excludedLabels.push(label)
        continue
      }
      axisFamilies.add(family)
    }
    graphs.push({
      type: visualization,
      dataKey: key,
      label,
      // Explicit stable color, seeded by the metric key: the bottom legend
      // reads the raw `graphs` prop (not the color-defaulted sortedGraphs),
      // so a graph without a color would render a white swatch.
      color: getLightColor(stringToColor(key)),
      numberType: type === "currency" ? "currency" : "default",
      ...(suffix !== undefined ? { unit: suffix, pluralizeUnit: false } : {}),
      yAxisId: family,
    })
  }

  return { graphs, excludedLabels }
}

export interface KPIGroupChartProps {
  title: string
  /** The group's rendered rows — the exact `config.data` its table shows. */
  rows: KpiRow[]
  /** ISO [start, end] per `week_N` column, from useConnect's customEditMeta. */
  columnBuckets: ColumnBucket[]
  /** Catalog-resolved metric defs for the group's configured keys, in order. */
  metricDefs: MetricDefinition[]
  visualization: ChartVisualization
  granularity: AggregationType
  showYoY: boolean
  isLoading?: boolean
  /** Rendered on the header's right side (the table/line/bar switcher). */
  headerControls?: ReactNode
  /**
   * When set, the chart height is user-resizable (bottom drag handle) and
   * remembered per user under this key: "explorer" for the Metrics Explorer,
   * "reportId:groupId" for tables-view groups. Absent = the fixed legacy
   * height and no handle.
   */
  sizeKey?: string
}

export default function KPIGroupChart({
  title,
  rows,
  columnBuckets,
  metricDefs,
  visualization,
  granularity,
  showYoY,
  isLoading,
  headerControls,
  sizeKey,
}: KPIGroupChartProps) {
  const chartData = useMemo(
    () => buildGroupChartData(rows, columnBuckets, showYoY),
    [rows, columnBuckets, showYoY],
  )
  const { graphs, excludedLabels } = useMemo(
    () => buildGroupChartGraphs(rows, metricDefs, visualization),
    [rows, metricDefs, visualization],
  )

  const { height, setHeight } = useChartHeight(sizeKey)
  // Live height while the handle is dragged; null when idle. Series animation
  // is suspended during the drag (recharts replays its 1.5s draw on every
  // size change) and resumes, with one normal draw, on release.
  const [dragHeight, setDragHeight] = useState<number | null>(null)
  const renderedHeight = dragHeight ?? height

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white p-2">{title}</h3>
        {headerControls}
      </div>
      <div className="bg-system-blue-800 border border-system-blue-700 rounded-system-lg p-6">
        {excludedLabels.length > 0 && (
          <div className="flex items-center flex-wrap gap-2 mb-4 px-3 py-2 bg-system-warning-500/10 border border-system-warning-500/60 rounded-system-md">
            <span className="text-sm text-system-warning-400">
              Not shown (2-axis limit):
            </span>
            {excludedLabels.map(label => (
              <Chip
                key={label}
                className="bg-system-blue-700 border border-system-blue-600 text-white text-xs"
              >
                {label}
              </Chip>
            ))}
          </div>
        )}
        <div style={{ height: renderedHeight }}>
          <ComposedChart
            metrics={chartData}
            graphs={graphs}
            compareMode={showYoY}
            // The REAL granularity: a weekly dashboard charts weekly buckets
            // (the retired modal's weekly→DAILY mapping was a bug).
            dataAggType={granularity}
            hideAggSelector
            hideTotal
            // Keep the group's configured metric order for draw order, bar
            // grouping, and the tooltip — no value-based resorting.
            graphsManualSort
            isInverted
            bottomLegend
            interactive
            state="idle"
            isLoading={isLoading}
            isAnimationActive={dragHeight === null}
            className="w-full h-full"
            sources={new Map()}
          />
        </div>
        {sizeKey !== undefined && (
          <ChartResizeHandle
            height={height}
            min={MIN_CHART_HEIGHT}
            max={MAX_CHART_HEIGHT}
            defaultHeight={DEFAULT_CHART_HEIGHT}
            onPreview={setDragHeight}
            onCommit={next => {
              setDragHeight(null)
              setHeight(next)
            }}
            className="mt-2 -mb-3"
          />
        )}
      </div>
    </div>
  )
}
